import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    linkedSignal,
    OnInit,
    signal,
} from '@angular/core';

import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { SelectSynopticPanelComponent } from '@connect-services/client/components/synoptic/select-synoptic-panel/select-synoptic-panel.component';
import { SynopticGraphComponent } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-graph.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { BroadcastChannelService, SynopticData } from '@connect-services/client/services/broadcast-channel.service';
import { FeatureSessionService } from '@connect-services/client/services/feature-session.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapPopupService } from '@connect-services/client/services/map-popup.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { FeatureSession } from '@connect-services/src/sdk/api';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingComponent, NoDataToDisplayComponent, SelectSynopticPanelComponent, SynopticGraphComponent],
    selector: 'app-synoptic',
    styleUrl: './synoptic.component.scss',
    templateUrl: './synoptic.component.html',
})
export class SynopticComponent implements OnInit {
    protected readonly isProjectEditModeActive = linkedSignal<boolean>(() =>
        this.projectEditModeService.isEditModeActive(),
    );
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly homeService = inject(HomeService);
    protected readonly projectEditModeService = inject(ProjectEditModeService);
    protected readonly selectSynopticService = inject(SelectSynopticService);

    private readonly broadcastEditModeChannel = new BroadcastChannel(BroadcastChannelNameEnum.EDIT_MODE);
    private readonly broadcastSynopticChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private readonly featureSessionId = signal<string | undefined>(undefined);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly featureSessionService = inject(FeatureSessionService);
    private readonly mapPopupService = inject(MapPopupService);

    public constructor() {
        effect(async () => {
            this.mapPopupService.closePopup();

            if (this.homeService.isExternalBoxPlanPage()) {
                return;
            }

            await this.selectSynopticService.loadSynopticData();
        });

        this.destroyRef.onDestroy(() => {
            if (this.broadcastChannelService.externalOpenInProgress()) {
                return;
            }
            this.reset();
        });

        if (!this.homeService.isExternalPage() && !this.homeService.isExternalBoxPlanPage()) {
            return;
        }

        window.addEventListener('beforeunload', this.handleBeforeUnload);

        if (this.homeService.isExternalPage() && !this.homeService.isExternalBoxPlanPage()) {
            this.broadcastSynopticChannel.postMessage(<BroadcastChannelEvent<null>>{
                isOpening: true,
                source: BroadcastChannelSourceEnum.GRAPH,
            });
        }

        if (this.homeService.isExternalBoxPlanPage()) {
            this.selectSynopticService.isSynopticDisplayed.set(true);
            this.selectSynopticService.isLoading.set(false);
        }

        this.broadcastSynopticChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<null | SynopticData>>,
        ): void => {
            this.handleBroadcastChannelEvents(event);
        };
        this.broadcastEditModeChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<{ isEditModeActive: boolean }>>,
        ): void => {
            this.checkIsEditModeEvent(event);
        };
    }

    public async ngOnInit(): Promise<void> {
        if (!this.homeService.isExternalPage()) {
            return;
        }

        this.featureSessionId.set(
            await this.featureSessionService.startFeatureSession(FeatureSession.FeatureTrackedEnum.SYNOPTIC_EXT, false),
        );
    }

    private checkBroadcastChannelClosingEvent(event: MessageEvent<BroadcastChannelEvent<null | SynopticData>>): void {
        if (event.data.message !== BroadcastChannelMessageEnum.CLOSE) {
            return;
        }

        this.reset();
        window.close();
    }

    private checkBroadCastChannelSynopticOpeningEvent(
        event: MessageEvent<BroadcastChannelEvent<null | SynopticData>>,
    ): void {
        if (
            event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE ||
            !event.data.isOpening ||
            this.selectSynopticService.isSynopticDisplayed()
        ) {
            return;
        }

        this.broadcastChannelService.openSelectedSynoptic(event.data);
    }

    private checkIsEditModeEvent(event: MessageEvent<BroadcastChannelEvent<{ isEditModeActive: boolean }>>): void {
        if (
            event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE ||
            !event.data.additionalData ||
            event.data.additionalData.isEditModeActive === this.isProjectEditModeActive()
        ) {
            return;
        }

        this.isProjectEditModeActive.set(event.data.additionalData!.isEditModeActive);
    }

    private checkOpenedSynopticEvent(event: MessageEvent<BroadcastChannelEvent<null | SynopticData>>): void {
        if (
            !this.homeService.isExternalPage() ||
            this.homeService.isExternalBoxPlanPage() ||
            event.data.message !== BroadcastChannelMessageEnum.CHECK_OPENED_WINDOW ||
            event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE
        ) {
            return;
        }

        this.broadcastSynopticChannel.postMessage(<BroadcastChannelEvent<null>>{
            message: BroadcastChannelMessageEnum.CHECK_OPENED_WINDOW,
            source: BroadcastChannelSourceEnum.GRAPH,
        });
    }

    private handleBeforeUnload = async (): Promise<void> => {
        if (this.homeService.isExternalBoxPlanPage()) {
            this.broadcastChannelService.closeExternalWindowBP(this.broadcastSynopticChannel);
        } else if (this.homeService.isExternalPage()) {
            this.broadcastChannelService.closeExternalWindowSynoptic(this.broadcastSynopticChannel);
        }

        this.broadcastSynopticChannel.close();

        if (!this.homeService.isExternalPage() || !this.featureSessionId()) {
            return;
        }

        await this.featureSessionService.endSession(this.featureSessionId()!, false);

        this.featureSessionId.set(undefined);
    };

    private handleBroadcastChannelEvents(event: MessageEvent<BroadcastChannelEvent<SynopticData | null>>): void {
        this.checkBroadcastChannelClosingEvent(event);
        this.checkBroadCastChannelSynopticOpeningEvent(event);
        this.checkOpenedSynopticEvent(event);
    }

    private reset(): void {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.broadcastSynopticChannel.close();

        if (!this.homeService.isExternalPage()) {
            this.selectSynopticService.resetAll();
        }
    }
}
